import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle,
  Users,
  Zap,
  Settings,
  Smartphone,
  Menu,
  X,
  AlertCircle,
} from "lucide-react";
import SignupForm, { SignupData } from "../SignupForm/SignupForm";
import LoginForm, { LoginUserData } from "../LoginForm/LoginForm";
import {
  ProgressivePricingPlan,
  ProgressivePlansResponse,
  ProgressivePricingType,
} from "../../types/types";
import { realAPI } from "../../services/realAPI";

interface LandingPageProps {
  onStartFree: () => void;
  onSignupComplete: (data: SignupData) => void;
  onLoginSuccess: (userData: LoginUserData) => void;
  showLoginByDefault?: boolean;
}

const LandingPage: React.FC<LandingPageProps> = ({
  onStartFree,
  onSignupComplete,
  onLoginSuccess,
  showLoginByDefault = false,
}) => {
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(showLoginByDefault);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [progressivePlans, setProgressivePlans] = useState<
    ProgressivePricingPlan[]
  >([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [plansError, setPlansError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Fetch progressive plans from real API
  useEffect(() => {
    const fetchProgressivePlans = async () => {
      try {
        setIsLoadingPlans(true);
        setPlansError(null);
        console.log("🚀 Fetching public progressive plans...");

        const response: ProgressivePlansResponse =
          await realAPI.public.getProgressivePlans();
        console.log("✅ Progressive plans fetched successfully:", response);

        if (response && response.data && response.data.plans) {
          setProgressivePlans(response.data.plans);
        } else {
          setPlansError("No progressive plans data received");
        }
      } catch (err) {
        console.error("❌ Error fetching progressive plans:", err);
        setPlansError("Failed to load pricing plans. Please try again later.");
      } finally {
        setIsLoadingPlans(false);
      }
    };

    fetchProgressivePlans();
  }, []);

  const handleSignupClick = () => {
    setShowSignupForm(true);
    setShowLoginForm(false);
  };

  const handleLoginClick = () => {
    setShowLoginForm(true);
    setShowSignupForm(false);
  };

  const handleSignupComplete = (data: SignupData) => {
    setShowSignupForm(false);
    onSignupComplete(data);
  };

  const handleLoginSuccess = (userData: LoginUserData) => {
    setShowLoginForm(false);
    onLoginSuccess(userData);
  };

  const handleCloseSignup = () => {
    setShowSignupForm(false);
  };

  const handleCloseLogin = () => {
    setShowLoginForm(false);
  };

  const handleSwitchToSignup = () => {
    setShowLoginForm(false);
    setShowSignupForm(true);
  };

  const handleSwitchToLogin = () => {
    setShowSignupForm(false);
    setShowLoginForm(true);
  };

  const handleForgotPassword = () => {
    setShowLoginForm(false);
    navigate("/forgot-password");
  };

  const features = [
    {
      icon: <Settings className="w-6 h-6 text-blue-600" />,
      title: "Easy Setup & Clean UI",
      description:
        "Get started in minutes with our intuitive interface designed for teams",
    },
    {
      icon: <Zap className="w-6 h-6 text-blue-600" />,
      title: "Drag-and-Drop Sprint Management",
      description:
        "Effortlessly move tasks through your workflow with smooth drag-and-drop",
    },
    {
      icon: <Users className="w-6 h-6 text-blue-600" />,
      title: "Team & Role Control",
      description: "Organize your team with proper roles and permissions",
    },
    {
      icon: <Smartphone className="w-6 h-6 text-blue-600" />,
      title: "Mobile Friendly",
      description: "Manage your projects on the go with our responsive design",
    },
  ];

  const faqs = [
    {
      question: "How does the progressive pricing work?",
      answer:
        "We use a progressive pricing model where you start with free tiers and pay only for what you use beyond the free limits. Each pricing tier is designed to scale with your team's growth.",
    },
    {
      question: "What are the free limits?",
      answer:
        "We offer generous free tiers for both users and stories to help teams get started. Check our pricing section above for current free tier limits and progressive pricing options.",
    },
    {
      question: "Can I upgrade or add more capacity anytime?",
      answer:
        "Yes! You can purchase additional capacity tiers at any time. Our progressive pricing model allows you to scale up as your team grows without complex plan changes.",
    },
    {
      question: "Is there a mobile app?",
      answer:
        "Our web application is fully responsive and works great on mobile devices. A dedicated mobile app is coming soon!",
    },
    {
      question: "Do you offer refunds?",
      answer:
        "Yes, we offer a 30-day money-back guarantee for all paid tiers. No questions asked.",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="text-2xl font-bold text-blue-600">Kanbello</div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                <a
                  href="#features"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium"
                >
                  Features
                </a>
                <a
                  href="#pricing"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium"
                >
                  Pricing
                </a>
                <a
                  href="#faq"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium"
                >
                  FAQ
                </a>
                <button
                  onClick={handleLoginClick}
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium"
                >
                  Login
                </button>
                <button
                  onClick={handleSignupClick}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  Sign Up Free
                </button>
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-600 hover:text-gray-900 focus:outline-none focus:text-gray-900 p-2"
              >
                {isMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {isMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-gray-200">
                <a
                  href="#features"
                  className="block text-gray-600 hover:text-gray-900 px-3 py-2 text-base font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Features
                </a>
                <a
                  href="#pricing"
                  className="block text-gray-600 hover:text-gray-900 px-3 py-2 text-base font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Pricing
                </a>
                <a
                  href="#faq"
                  className="block text-gray-600 hover:text-gray-900 px-3 py-2 text-base font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  FAQ
                </a>
                <button
                  onClick={() => {
                    handleLoginClick();
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left text-gray-600 hover:text-gray-900 px-3 py-2 text-base font-medium"
                >
                  Login
                </button>
                <button
                  onClick={() => {
                    handleSignupClick();
                    setIsMenuOpen(false);
                  }}
                  className="block w-full bg-blue-600 text-white px-3 py-2 rounded-lg text-base font-medium hover:bg-blue-700 transition-colors mt-2"
                >
                  Sign Up Free
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Simple, Powerful Kanban Board
              <span className="text-blue-600"> for Small Teams</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Plan sprints, manage tasks, and collaborate easily — without the
              complexity of enterprise tools. Get your team organized in
              minutes, not hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleSignupClick}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors shadow-lg"
              >
                Start for Free
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              No credit card required • Start free and scale as you grow
            </p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Teams Choose KanbanPro
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built specifically for small to medium teams who need powerful
              project management without the enterprise complexity.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="mx-auto w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Detailed Features */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Manage Projects
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">
                    Feature
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    Organization & Team Setup
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    Create admin and users in structured roles
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    Sprint & Backlog
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    Plan agile sprints and drag stories from backlog
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    Drag-and-Drop Kanban
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    Move stories across workflow stages easily
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    Story Management
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    Assign, estimate, comment, and track progress
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    Mobile Friendly UI
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    Manage work on the go
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Start free, pay only as your team grows — no complex tiers or
              hidden fees.
            </p>
          </div>

          {/* Progressive Pricing Model */}
          {isLoadingPlans ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Loading pricing information...</p>
            </div>
          ) : plansError ? (
            <div className="max-w-3xl mx-auto">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                <h3 className="text-lg font-medium text-red-800 mb-2">
                  Unable to load pricing
                </h3>
                <p className="text-red-600 mb-4">{plansError}</p>
                {/* Fallback to basic pricing display */}
                <div className="grid md:grid-cols-2 gap-8 mt-6">
                  <div className="bg-white rounded-2xl border-2 border-gray-200 p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                      Stories
                    </h3>
                    <ul className="space-y-3 mb-8">
                      <li className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-700">
                          First 50 stories: Free
                        </span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-700">
                          Each additional 50 stories: $10
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-white rounded-2xl border-2 border-gray-200 p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                      Users
                    </h3>
                    <ul className="space-y-3 mb-8">
                      <li className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-700">
                          First 5 users: Free
                        </span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-700">
                          Each additional 5 users: $10
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            (() => {
              // Group plans by type
              const storyPlans = progressivePlans
                .filter((plan) => plan.type === ProgressivePricingType.STORIES)
                .sort((a, b) => a.planLimit - b.planLimit);
              const userPlans = progressivePlans
                .filter((plan) => plan.type === ProgressivePricingType.USERS)
                .sort((a, b) => a.planLimit - b.planLimit);

              return (
                <div className="max-w-3xl mx-auto grid md:grid-cols-2 gap-8">
                  {/* Stories Pricing */}
                  <div className="rounded-2xl border-2 border-gray-200 p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                      Stories
                    </h3>
                    <ul className="space-y-3 mb-8">
                      {storyPlans.map((plan, index) => (
                        <li key={plan.id} className="flex items-center">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                          <span className="text-gray-700">
                            {index === 0 && plan.priceCents === 0
                              ? `First ${plan.planLimit} stories: Free`
                              : `${plan.planLimit} stories: ${
                                  plan.priceCents === 0
                                    ? "Free"
                                    : `$${(plan.priceCents / 100).toFixed(0)}`
                                }`}
                          </span>
                        </li>
                      ))}
                    </ul>
                    {storyPlans.length > 1 && (
                      <div className="space-y-2">
                        <p className="text-sm text-gray-600">
                          Example:{" "}
                          {Math.max(...storyPlans.map((p) => p.planLimit)) + 20}{" "}
                          stories = $
                          {storyPlans
                            .filter((p) => p.priceCents > 0)
                            .slice(0, 2)
                            .reduce(
                              (total, plan) => total + plan.priceCents / 100,
                              0
                            )}
                        </p>
                        <p className="text-sm text-gray-600">
                          (Note: closed/deleted stories still count as they
                          consume resources)
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Users Pricing */}
                  <div className="rounded-2xl border-2 border-gray-200 p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                      Users
                    </h3>
                    <ul className="space-y-3 mb-8">
                      {userPlans.map((plan, index) => (
                        <li key={plan.id} className="flex items-center">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                          <span className="text-gray-700">
                            {index === 0 && plan.priceCents === 0
                              ? `First ${plan.planLimit} users: Free`
                              : `${plan.planLimit} users: ${
                                  plan.priceCents === 0
                                    ? "Free"
                                    : `$${(plan.priceCents / 100).toFixed(0)}`
                                }`}
                          </span>
                        </li>
                      ))}
                    </ul>
                    {userPlans.length > 1 && (
                      <p className="text-sm text-gray-600">
                        Example:{" "}
                        {Math.max(...userPlans.map((p) => p.planLimit)) + 2}{" "}
                        users = $
                        {userPlans
                          .filter((p) => p.priceCents > 0)
                          .slice(0, 2)
                          .reduce(
                            (total, plan) => total + plan.priceCents / 100,
                            0
                          )}
                      </p>
                    )}
                  </div>
                </div>
              );
            })()
          )}

          {/* CTA */}
          <div className="text-center mt-12">
            <button
              onClick={handleSignupClick}
              className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-medium transition-colors"
            >
              Get Started Free
            </button>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div id="faq" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {faq.question}
                </h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-blue-600 py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Team's Productivity?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of teams already using KanbanPro to streamline their
            workflow
          </p>
          <button
            onClick={handleSignupClick}
            className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-100 transition-colors shadow-lg"
          >
            Start Free - No Credit Card
          </button>
          <p className="text-sm text-blue-200 mt-4">
            Start free and upgrade when you're ready • Progressive pricing
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="text-2xl font-bold text-white mb-4">
                KanbanPro
              </div>
              <p className="text-gray-400">
                Simple, powerful project management for modern teams.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-gray-400">
                <li>Features</li>
                <li>Pricing</li>
                <li>Integrations</li>
                <li>API</li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-gray-400">
                <li>About</li>
                <li>Blog</li>
                <li>Careers</li>
                <li>Contact</li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li>Help Center</li>
                <li>Documentation</li>
                <li>Status</li>
                <li>Security</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 KanbanPro. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Signup Form Modal */}
      <SignupForm
        isOpen={showSignupForm}
        onClose={handleCloseSignup}
        onSignupComplete={handleSignupComplete}
        onSwitchToLogin={handleSwitchToLogin}
      />

      {/* Login Form Modal */}
      <LoginForm
        isOpen={showLoginForm}
        onClose={handleCloseLogin}
        onLoginSuccess={handleLoginSuccess}
        onSwitchToSignup={handleSwitchToSignup}
        onForgotPassword={handleForgotPassword}
      />
    </div>
  );
};

export default LandingPage;
